import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/react/portainer/services/axios/axios';
import { buildStackUrl } from '@/react/common/stacks/queries/buildUrl';
import { queryKeys } from '@/react/common/stacks/queries/query-keys';
import { useCurrentUser } from '@/react/hooks/useUser';
import { saveGitCredentialsIfNeeded } from '@/react/portainer/account/git-credentials/queries/useCreateGitCredentialsMutation';
import {
  GitAuthModel,
  AutoUpdateResponse,
} from '@/react/portainer/gitops/types';
import { withGlobalError } from '@/react-tools/react-query';

import { EnvVar } from '@@/form-components/EnvironmentVariablesFieldset/types';

import { AuthTypeOption } from '../../account/git-credentials/types';

export interface GitStackPayload {
  env: Array<EnvVar>;
  prune?: boolean;
  RepositoryURL?: string;
  ConfigFilePath?: string;
  RepositoryReferenceName?: string;
  RepositoryAuthentication?: boolean;
  RepositoryGitCredentialID?: number;
  RepositoryUsername?: string;
  RepositoryPassword?: string;
  RepositoryAuthorizationType?: AuthTypeOption;
  AutoUpdate?: AutoUpdateResponse | null;
  TLSSkipVerify?: boolean;
  Registries?: number[];
  AdditionalFiles?: string[];
  HelmChartPath?: string;
  HelmValuesFiles?: string[];
  Atomic?: boolean;
}

export async function updateGitStackSettings(
  stackId: number,
  endpointId: number,
  payload: GitStackPayload
) {
  try {
    const { data } = await axios.post(buildStackUrl(stackId, 'git'), payload, {
      params: { endpointId },
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error);
  }
}

export function useUpdateGitStackSettings() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({
      stackId,
      endpointId,
      payload: { SaveCredential, NewCredentialName, ...payload },
    }: {
      stackId: number;
      endpointId: number;
      payload: GitStackPayload & {
        SaveCredential?: boolean;
        NewCredentialName?: string;
      };
    }) => {
      const gitAuthModel: GitAuthModel = {
        RepositoryAuthentication: payload.RepositoryAuthentication,
        RepositoryUsername: payload.RepositoryUsername,
        RepositoryPassword: payload.RepositoryPassword,
        RepositoryGitCredentialID: payload.RepositoryGitCredentialID,
        RepositoryAuthorizationType: payload.RepositoryAuthorizationType,
        SaveCredential,
        NewCredentialName,
      };
      const resolvedAuth = await saveGitCredentialsIfNeeded(
        user.Id,
        gitAuthModel
      );
      return updateGitStackSettings(stackId, endpointId, {
        ...payload,
        RepositoryAuthentication: resolvedAuth.RepositoryAuthentication,
        RepositoryGitCredentialID: resolvedAuth.RepositoryGitCredentialID,
        RepositoryUsername: resolvedAuth.RepositoryUsername,
        RepositoryPassword: resolvedAuth.RepositoryPassword,
        RepositoryAuthorizationType: resolvedAuth.RepositoryAuthorizationType,
      });
    },
    onSuccess: (_, { stackId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stack(stackId),
        exact: true,
      });
    },
    ...withGlobalError('Unable to save stack settings'),
  });
}
